import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Settings as SettingsIcon,
  User,
  Globe,
  Sun,
  Moon,
  DollarSign,
  Trash2,
  CheckCircle2,
  ShieldAlert,
  Lock,
  KeyRound,
  Eye,
  EyeOff,
  LogOut,
  ShieldCheck,
  AlertCircle,
  Cloud,
  RefreshCw,
  Fingerprint,
  Database,
  Download,
  Upload,
  ExternalLink,
  Check,
} from 'lucide-react';
import { Currency, Theme } from '../../types';
import { SetPinModal } from '../security/SetPinModal';
import { StorageService } from '../../lib/storage';
import {
  getStoredSupabaseConfig,
  saveSupabaseConfig,
  clearSupabaseConfig,
  testSupabaseConnection,
} from '../../lib/supabaseClient';

export const SettingsView: React.FC = () => {
  const {
    user,
    updateUser,
    language,
    theme,
    setTheme,
    currency,
    setCurrency,
    resetData,
    logout,
    changePassword,
    triggerConfetti,
    t,
    syncState,
    syncNow,
    pullNow,
    setSyncKey,
    isAppLockEnabled,
    isBiometricsEnabled,
    isBiometricsAvailable,
    lockApp,
    toggleBiometrics,
    autoLockTimeout,
    setAutoLockTimeout,
  } = useApp();

  const isBn = language === 'bn';

  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pinModalMode, setPinModalMode] = useState<'setup' | 'change' | 'disable'>('setup');

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isSaved, setIsSaved] = useState(false);

  // Cloud Sync state
  const [syncKeyInput, setSyncKeyInput] = useState(syncState?.syncKey || 'user_tahsin');
  const [isPushingNow, setIsPushingNow] = useState(false);
  const [isPullingNow, setIsPullingNow] = useState(false);

  // Password Change State
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Master PIN for Notes Lock
  const [masterPin, setMasterPin] = useState<string>(() => {
    return localStorage.getItem('mylife_notes_master_pin') || localStorage.getItem('mylife_master_pin') || '1234';
  });
  const [newPinInput, setNewPinInput] = useState<string>('');
  const [showPin, setShowPin] = useState(false);
  const [pinSavedMessage, setPinSavedMessage] = useState(false);

  // Supabase Configuration State
  const initialSupabaseConfig = getStoredSupabaseConfig();
  const [supabaseUrlInput, setSupabaseUrlInput] = useState(initialSupabaseConfig.url || '');
  const [supabaseKeyInput, setSupabaseKeyInput] = useState(initialSupabaseConfig.key || '');
  const [supabaseStatusMsg, setSupabaseStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isTestingSupabase, setIsTestingSupabase] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleExportBackup = () => {
    const json = StorageService.exportBackupJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mylife_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    triggerConfetti();
  };

  const handleImportBackup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        const success = StorageService.importBackupJSON(content);
        if (success) {
          alert(
            isBn
              ? 'সফলভাবে সমস্ত ডেটা রিস্টোর হয়েছে! অ্যাপ রিলোড হচ্ছে...'
              : 'All data successfully restored! Reloading app...'
          );
          window.location.reload();
        } else {
          alert(isBn ? 'ফাইলটি সঠিক ব্যাকআপ ফাইল নয়!' : 'Invalid backup file format!');
        }
      }
    };
    reader.readAsText(file);
  };

  const handleSaveSupabaseConfig = async () => {
    if (!supabaseUrlInput.trim() || !supabaseKeyInput.trim()) {
      clearSupabaseConfig();
      setSupabaseStatusMsg({
        type: 'success',
        text: isBn ? 'Supabase কনফিগারেশন মুছে ফেলা হয়েছে (Local Mode)।' : 'Supabase config cleared (Local Mode).',
      });
      return;
    }
    saveSupabaseConfig(supabaseUrlInput.trim(), supabaseKeyInput.trim());
    setIsTestingSupabase(true);
    const res = await testSupabaseConnection();
    setIsTestingSupabase(false);
    if (res.success) {
      setSupabaseStatusMsg({
        type: 'success',
        text: isBn ? 'সফলভাবে Supabase ক্লাউডে কানেক্ট হয়েছে!' : 'Connected to Supabase successfully!',
      });
      triggerConfetti();
    } else {
      setSupabaseStatusMsg({
        type: 'error',
        text: isBn
          ? `কানেকশন এরর: ${res.error || 'চেক করুন URL ও Key সঠিক কি না'}`
          : `Connection Error: ${res.error || 'Please verify URL and Key'}`,
      });
    }
  };

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateUser({ name: name.trim(), email: email.trim() });
    setIsSaved(true);
    triggerConfetti();
    setTimeout(() => setIsSaved(false), 3000);
  };

  const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setPasswordError(null);
    setPasswordSuccess(null);

    if (!oldPassword.trim()) {
      setPasswordError(isBn ? 'বর্তমান পাসওয়ার্ডটি প্রদান করুন!' : 'Please enter your current password!');
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError(isBn ? 'নতুন পাসওয়ার্ড প্রদান করুন!' : 'Please enter a new password!');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(isBn ? 'নতুন পাসওয়ার্ড এবং কনফার্ম পাসওয়ার্ড মিলছে না!' : 'New password and confirm password do not match!');
      return;
    }

    const res = await changePassword(oldPassword.trim(), newPassword.trim());
    if (res.success) {
      setPasswordSuccess(isBn ? 'পাসওয়ার্ড সফলভাবে পরিবর্তন করা হয়েছে!' : 'Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(null), 4000);
    } else {
      setPasswordError(res.error || (isBn ? 'পাসওয়ার্ড পরিবর্তন ব্যর্থ হয়েছে!' : 'Failed to change password!'));
    }
  };

  const handleSavePin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPinInput.trim()) return;
    const cleanPin = newPinInput.trim();
    localStorage.setItem('mylife_notes_master_pin', cleanPin);
    localStorage.setItem('mylife_master_pin', cleanPin);
    StorageService.setItem('mylife_notes_master_pin', cleanPin);
    setMasterPin(cleanPin);
    setNewPinInput('');
    setPinSavedMessage(true);
    triggerConfetti();
    setTimeout(() => setPinSavedMessage(false), 3000);
  };

  const handleLogoutClick = () => {
    const confirmMsg = isBn ? 'আপনি কি নিশ্চিত যে আপনি লগআউট করতে চান?' : 'Are you sure you want to sign out?';
    if (window.confirm(confirmMsg)) {
      logout();
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in pb-20">
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5 ${isBn ? 'font-bangla' : ''}`}>
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <SettingsIcon className="w-5 h-5" />
          </div>
          <span>{isBn ? 'সেটিংস ও অ্যাকাউন্ট ম্যানেজমেন্ট' : 'Settings & Account Management'}</span>
        </h1>
      </div>

      {/* Profile Section */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
        <h3 className={`text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
          <User className="w-4 h-4 text-emerald-500" />
          <span>{isBn ? 'প্রোফাইল তথ্য' : 'Profile Information'}</span>
        </h3>

        <form onSubmit={handleProfileSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">{isBn ? 'পূর্ণ নাম' : 'Full Name'}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">{isBn ? 'ইমেইল অ্যাড্রেস' : 'Email Address'}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              type="submit"
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center gap-1.5 transition-all"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{isBn ? 'সেভ করুন' : 'Save Changes'}</span>
            </button>

            {isSaved && (
              <span className="text-xs font-bold text-emerald-500 flex items-center gap-1 animate-fade-in">
                <CheckCircle2 className="w-4 h-4" />
                <span>{isBn ? 'প্রোফাইল সেভ হয়েছে!' : 'Profile saved successfully!'}</span>
              </span>
            )}
          </div>
        </form>
      </div>

      {/* ACCOUNT PASSWORD CHANGE SECTION */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
        <h3 className={`text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
          <KeyRound className="w-4 h-4 text-amber-500" />
          <span>{isBn ? 'অ্যাকাউন্ট পাসওয়ার্ড পরিবর্তন' : 'Change Account Password'}</span>
        </h3>
        <p className="text-xs text-slate-500">
          {isBn
            ? 'আপনার অ্যাপে সাইন ইন করার প্রধান পাসওয়ার্ড পরিবর্তন করুন।'
            : 'Update your primary password used to sign in across all devices.'}
        </p>

        {passwordError && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-600 dark:text-rose-400 text-xs font-bold flex items-center gap-2 animate-fade-in">
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
            <span>{passwordError}</span>
          </div>
        )}

        {passwordSuccess && (
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center gap-2 animate-fade-in">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
            <span>{passwordSuccess}</span>
          </div>
        )}

        <form onSubmit={handlePasswordChangeSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Current Password */}
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                {isBn ? 'বর্তমান পাসওয়ার্ড' : 'Current Password'}
              </label>
              <div className="relative">
                <input
                  type={showOldPwd ? 'text' : 'password'}
                  required
                  placeholder="••••••••"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white pr-8 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={() => setShowOldPwd(!showOldPwd)}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  {showOldPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                {isBn ? 'নতুন পাসওয়ার্ড' : 'New Password'}
              </label>
              <div className="relative">
                <input
                  type={showNewPwd ? 'text' : 'password'}
                  required
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white pr-8 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd(!showNewPwd)}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  {showNewPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                {isBn ? 'পাসওয়ার্ড নিশ্চিত করুন' : 'Confirm Password'}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPwd ? 'text' : 'password'}
                  required
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white pr-8 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-md shadow-amber-500/20 flex items-center gap-1.5 transition-all active:scale-95"
            >
              <KeyRound className="w-3.5 h-3.5" />
              <span>{isBn ? 'পাসওয়ার্ড আপডেট করুন' : 'Update Password'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* App Lock & Biometrics Security Section */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h3 className={`text-sm sm:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
                <span>{isBn ? 'অ্যাপ লক ও বায়োমেট্রিক নিরাপত্তা' : 'App Lock & Biometric Security'}</span>
              </h3>
              <p className="text-xs text-slate-500">
                {isBn
                  ? 'অ্যাপে ৪-সংখ্যার পিন কোড এবং ফিঙ্গারপ্রিন্ট লক সেট করুন।'
                  : 'Protect your entire application with a 4-digit PIN and Fingerprint.'}
              </p>
            </div>
          </div>

          {/* Toggle Switch */}
          <button
            type="button"
            onClick={() => {
              if (isAppLockEnabled) {
                setPinModalMode('disable');
                setIsPinModalOpen(true);
              } else {
                setPinModalMode('setup');
                setIsPinModalOpen(true);
              }
            }}
            className={`w-12 h-6 rounded-full transition-colors relative flex items-center p-0.5 cursor-pointer ${
              isAppLockEnabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
                isAppLockEnabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {isAppLockEnabled ? (
          <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-slate-800">
            {/* Status & Change PIN row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-500">
                  <Lock className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 dark:text-white block">
                    {isBn ? 'অ্যাপ লক সক্রিয় রয়েছে' : 'App Lock is Active'}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {isBn ? 'আপনার ডেটা ৪-ডিজিট পিন দ্বারা সুরক্ষিত' : 'Secured with a 4-digit PIN code'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPinModalMode('change');
                    setIsPinModalOpen(true);
                  }}
                  className="px-3.5 py-1.5 rounded-xl border border-slate-300 dark:border-slate-700 hover:border-emerald-500 text-slate-700 dark:text-slate-300 text-xs font-bold transition-colors flex items-center gap-1.5"
                >
                  <KeyRound className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{isBn ? 'পিন পরিবর্তন' : 'Change PIN'}</span>
                </button>

                <button
                  type="button"
                  onClick={lockApp}
                  className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                >
                  <Lock className="w-3.5 h-3.5 text-amber-400" />
                  <span>{isBn ? 'এখনই লক করুন' : 'Lock Now'}</span>
                </button>
              </div>
            </div>

            {/* Fingerprint / Biometric Row */}
            <div className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-500">
                  <Fingerprint className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 dark:text-white block">
                    {isBn ? 'ফিঙ্গারপ্রিন্ট আনলক' : 'Fingerprint / Biometric Unlock'}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {isBiometricsAvailable
                      ? (isBn ? 'ফোনের ফিঙ্গারপ্রিন্ট সেন্সর ব্যবহার করুন' : 'Unlock using device fingerprint sensor')
                      : (isBn ? 'আপনার ব্রাউজার বা ডিভাইসে বায়োমেট্রিক সাপোর্ট নেই' : 'Biometrics not supported on this device/browser')}
                  </span>
                </div>
              </div>

              {isBiometricsAvailable ? (
                <button
                  type="button"
                  onClick={async () => {
                    const res = await toggleBiometrics(!isBiometricsEnabled);
                    if (!res.success && res.error) {
                      alert(res.error);
                    }
                  }}
                  className={`w-11 h-6 rounded-full transition-colors relative flex items-center p-0.5 cursor-pointer ${
                    isBiometricsEnabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
                      isBiometricsEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              ) : (
                <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-500">
                  {isBn ? 'অনুপলব্ধ' : 'Unavailable'}
                </span>
              )}
            </div>

            {/* Auto-Lock Timeout Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800">
              <div>
                <span className="text-xs font-bold text-slate-900 dark:text-white block">
                  {isBn ? 'অটো-লক সময়সীমা' : 'Auto-Lock Timeout'}
                </span>
                <span className="text-[11px] text-slate-500">
                  {isBn ? 'অ্যাপ থেকে অন্য কোথাও গেলে কখন লক হবে' : 'Lock app when left in background'}
                </span>
              </div>

              <select
                value={autoLockTimeout}
                onChange={(e) => setAutoLockTimeout(Number(e.target.value))}
                className="px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value={0}>{isBn ? 'সাথে সাথে (Immediately)' : 'Immediately'}</option>
                <option value={60000}>{isBn ? '১ মিনিট পর' : 'After 1 minute'}</option>
                <option value={300000}>{isBn ? '৫ মিনিট পর' : 'After 5 minutes'}</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {isBn ? 'অ্যাপ লক বর্তমানে নিষ্ক্রিয় রয়েছে।' : 'App lock is currently turned off.'}
            </span>
            <button
              type="button"
              onClick={() => {
                setPinModalMode('setup');
                setIsPinModalOpen(true);
              }}
              className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-xs"
            >
              {isBn ? 'লক চালু করুন' : 'Enable Lock'}
            </button>
          </div>
        )}
      </div>

      {/* Master Notes PIN Lock */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Lock className="w-4 h-4 text-purple-500" />
            <span>{isBn ? 'নোটস সিকিউরিটি পিন (Notes PIN)' : 'Notes Security PIN Lock'}</span>
          </h3>
          <span className="text-xs font-mono bg-purple-500/10 text-purple-600 dark:text-purple-400 px-2.5 py-1 rounded-lg font-bold">
            {isBn ? 'বর্তমান পিন:' : 'Current PIN:'} {showPin ? masterPin : '••••'}
          </span>
        </div>

        <p className="text-xs text-slate-500">
          {isBn
            ? 'আপনার ব্যক্তিগত নোট সুরক্ষিত রাখতে ৪-সংখ্যার মাস্টার পিন কোড সেট করুন।'
            : 'Set a 4-digit PIN code to lock and secure confidential notes.'}
        </p>

        <form onSubmit={handleSavePin} className="flex flex-wrap items-center gap-2 pt-1">
          <div className="relative flex-1 min-w-[200px]">
            <input
              type={showPin ? 'text' : 'password'}
              maxLength={6}
              placeholder={isBn ? 'নতুন পিন কোড দিন...' : 'Enter new PIN code...'}
              value={newPinInput}
              onChange={(e) => setNewPinInput(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white tracking-widest focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={!newPinInput.trim()}
            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-md shadow-purple-500/25 flex items-center justify-center gap-1.5 transition-all"
          >
            <KeyRound className="w-3.5 h-3.5" />
            <span>{isBn ? 'পিন পরিবর্তন করুন' : 'Change PIN'}</span>
          </button>
        </form>

        {pinSavedMessage && (
          <span className="text-xs font-bold text-emerald-500 flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" />
            <span>{isBn ? 'মাস্টার পিন সফলভাবে আপডেট হয়েছে!' : 'Master PIN updated successfully!'}</span>
          </span>
        )}
      </div>

      {/* LOCALHOST <-> LIVE DATA MIGRATION & BACKUP SECTION */}
      <div className="p-6 bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-transparent dark:from-emerald-950/30 dark:via-teal-950/10 border-2 border-emerald-500/30 dark:border-emerald-500/30 rounded-3xl shadow-sm space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={`text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
              <Database className="w-4 h-4 text-emerald-500" />
              <span>{isBn ? 'লোকালহোস্ট ও লাইভ সাইটের ডেটা আদান-প্রদান (Data Transfer)' : 'Localhost ↔ Live Data Transfer & Backup'}</span>
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
              {isBn
                ? 'লোকালহোস্টে সেভ করা সমস্ত হিসাব, নোটস, টাস্ক, রিপোর্ট ও প্ল্যান এক ক্লিকে লাইভ ওয়েবসাইটে নিয়ে যান।'
                : 'Migrate and sync all your localhost finances, notes, tasks, reports, and monthly plans to your live website with 1 click.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          {/* Step 1: Export from Localhost */}
          <div className="p-4 bg-white dark:bg-[#111827] rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200">
              <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[11px]">1</span>
              <span>{isBn ? 'লোকালহোস্ট থেকে ব্যাকআপ নিন' : 'Step 1: Download from Localhost'}</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              {isBn
                ? 'আপনার লোকালহোস্টের সমস্ত ডাটা (হিসাব, টাস্ক, রিপোর্ট, নোটস) একটি ফাইলে ডাউনলোড করুন।'
                : 'Export all current accounts, transactions, reports and settings to a JSON backup file.'}
            </p>
            <button
              type="button"
              onClick={handleExportBackup}
              className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/20 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <Download className="w-4 h-4" />
              <span>{isBn ? 'সব ডেটা ব্যাকআপ ডাউনলোড করুন' : 'Export All Data (JSON)'}</span>
            </button>
          </div>

          {/* Step 2: Import into Live */}
          <div className="p-4 bg-white dark:bg-[#111827] rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200">
              <span className="w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center text-[11px]">2</span>
              <span>{isBn ? 'লাইভ ওয়েবসাইটে ডেটা রিস্টোর করুন' : 'Step 2: Restore in Live Website'}</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              {isBn
                ? 'লাইভ সাইটে এসে এই বাটনে ক্লিক করে ফাইলটি সিলেক্ট করলেই সব ডেটা মুহূর্তেই চলে আসবে।'
                : 'Open your live deployed website, select the backup file, and all localhost data will appear instantly.'}
            </p>
            <input
              type="file"
              ref={fileInputRef}
              accept=".json"
              onChange={handleImportBackup}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-2.5 px-4 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-xs font-bold shadow-md shadow-teal-500/20 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <Upload className="w-4 h-4" />
              <span>{isBn ? 'ফাইল সিলেক্ট করে রিস্টোর করুন' : 'Import / Restore Backup'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* CLOUD DATABASE & REALTIME SYNC SECTION */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className={`text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
              <Cloud className="w-4 h-4 text-emerald-500" />
              <span>{isBn ? 'ক্লাউড ডেটাবেস ও রিয়েলটাইম সিঙ্ক' : 'Cloud Database & Realtime Sync'}</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isBn
                ? 'সরাসরি রিয়েলটাইমে ক্লাউডে ডাটা সিঙ্ক রাখতে আপনার Supabase প্রজেক্ট কানেক্ট করুন।'
                : 'Connect your Supabase project to automatically sync data across all devices and live.'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                syncState.provider === 'supabase' && (syncState.status === 'synced' || syncState.status === 'idle')
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                  : syncState.status === 'syncing'
                  ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/30'
                  : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${
                syncState.provider === 'supabase' && (syncState.status === 'synced' || syncState.status === 'idle')
                  ? 'bg-emerald-500 animate-pulse'
                  : syncState.status === 'syncing'
                  ? 'bg-blue-500 animate-spin'
                  : 'bg-amber-500'
              }`} />
              <span>
                {syncState.provider === 'supabase'
                  ? (syncState.status === 'syncing'
                      ? isBn ? 'সিঙ্ক হচ্ছে...' : 'Syncing...'
                      : isBn ? 'Supabase সংযুক্ত' : 'Supabase Connected')
                  : isBn ? 'লোকাল মোড (Not Configured)' : 'Local Mode (Not Configured)'}
              </span>
            </span>
          </div>
        </div>

        {/* Sync Account Key & Info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-800 text-xs">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              {isBn ? 'সিঙ্ক অ্যাকাউন্ট কি (Sync Key)' : 'Sync Account Key'}
            </span>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={syncKeyInput}
                onChange={(e) => setSyncKeyInput(e.target.value)}
                className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <button
                type="button"
                onClick={() => {
                  if (syncKeyInput.trim()) {
                    setSyncKey(syncKeyInput.trim());
                    triggerConfetti();
                  }
                }}
                className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold whitespace-nowrap"
              >
                {isBn ? 'সেভ' : 'Save'}
              </button>
            </div>
          </div>

          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              {isBn ? 'ডেটাবেস ইঞ্জিন' : 'Database Engine'}
            </span>
            <div className="font-bold text-slate-800 dark:text-slate-200 pt-1.5 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${syncState.provider === 'supabase' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
              <span>{syncState.provider === 'supabase' ? 'Supabase PostgreSQL' : 'Local Browser Storage'}</span>
            </div>
          </div>

          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              {isBn ? 'সর্বশেষ সিঙ্ক' : 'Last Synced'}
            </span>
            <div className="font-bold text-slate-800 dark:text-slate-200 pt-1.5">
              {syncState.lastSyncedAt || (isBn ? 'এখনো সিঙ্ক হয়নি' : 'Not yet synced')}
            </div>
          </div>
        </div>

        {/* Supabase Credentials Inputs */}
        <div className="p-4 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-200/70 dark:border-slate-800/70 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
              {isBn ? 'Supabase ক্লাউড প্রজেক্ট কনফিগারেশন' : 'Supabase Cloud Project Setup'}
            </span>
            <a
              href="https://supabase.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1"
            >
              <span>{isBn ? 'ফ্রি অ্যাকাউন্ট খুলুন' : 'Get Free Supabase'}</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                Project URL (e.g. https://xyz.supabase.co)
              </label>
              <input
                type="text"
                placeholder="https://your-project.supabase.co"
                value={supabaseUrlInput}
                onChange={(e) => setSupabaseUrlInput(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                Anon Public API Key
              </label>
              <input
                type="password"
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpX..."
                value={supabaseKeyInput}
                onChange={(e) => setSupabaseKeyInput(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>

          {supabaseStatusMsg && (
            <div
              className={`p-2.5 rounded-xl text-xs font-bold flex items-center gap-2 ${
                supabaseStatusMsg.type === 'success'
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                  : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30'
              }`}
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{supabaseStatusMsg.text}</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              disabled={isTestingSupabase}
              onClick={handleSaveSupabaseConfig}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-white text-white dark:text-slate-900 rounded-xl text-xs font-bold shadow-xs transition-all flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
              <span>{isTestingSupabase ? (isBn ? 'টেস্ট করা হচ্ছে...' : 'Testing...') : (isBn ? 'সেভ ও টেস্ট করুন' : 'Save & Test Connection')}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="button"
            disabled={isPushingNow}
            onClick={async () => {
              setIsPushingNow(true);
              try {
                const ok = await syncNow();
                if (ok) triggerConfetti();
              } finally {
                setIsPushingNow(false);
              }
            }}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl text-xs font-bold shadow-sm flex items-center gap-2 transition-all active:scale-95"
          >
            <Cloud className={`w-3.5 h-3.5 ${isPushingNow ? 'animate-spin' : ''}`} />
            <span>{isPushingNow ? (isBn ? 'আপলোড হচ্ছে...' : 'Pushing...') : (isBn ? 'এখনই ক্লাউডে আপলোড করুন' : 'Push to Cloud')}</span>
          </button>

          <button
            type="button"
            disabled={isPullingNow}
            onClick={async () => {
              setIsPullingNow(true);
              try {
                const ok = await pullNow(true);
                if (ok) triggerConfetti();
              } finally {
                setIsPullingNow(false);
              }
            }}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 flex items-center gap-2 transition-all active:scale-95"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isPullingNow ? 'animate-spin' : ''}`} />
            <span>{isPullingNow ? (isBn ? 'নামানো হচ্ছে...' : 'Pulling...') : (isBn ? 'ক্লাউড থেকে ডেটা নামান' : 'Pull from Cloud')}</span>
          </button>
        </div>
      </div>

      {/* Preferences Section */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-6">
        <h3 className={`text-sm font-bold text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
          {isBn ? 'অ্যাপ পছন্দসমূহ' : 'App Preferences'}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Currency */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-500">{t.currency}</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white"
            >
              <option value="BDT">BDT (৳) - Bangladeshi Taka</option>
              <option value="USD">USD ($) - US Dollar</option>
              <option value="EUR">EUR (€) - Euro</option>
              <option value="GBP">GBP (£) - British Pound</option>
              <option value="INR">INR (₹) - Indian Rupee</option>
              <option value="CAD">CAD (C$) - Canadian Dollar</option>
            </select>
          </div>

          {/* Theme */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-500">{t.theme}</label>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as Theme)}
              className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white"
            >
              <option value="system">Follow System (Auto)</option>
              <option value="dark">{t.darkMode}</option>
              <option value="light">{t.lightMode}</option>
            </select>
          </div>
        </div>
      </div>

      {/* SIGN OUT / LOGOUT SECTION */}
      <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className={`text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
            <LogOut className="w-4 h-4 text-rose-500" />
            <span>{isBn ? 'অ্যাকাউন্ট লগআউট (Sign Out)' : 'Account Sign Out'}</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {isBn ? 'বর্তমান অ্যাকাউন্ট:' : 'Active Account:'} <strong className="text-slate-800 dark:text-slate-200">{user?.email || 'tahsinmiftah@proton.me'}</strong>
          </p>
        </div>

        <button
          onClick={handleLogoutClick}
          className="px-5 py-2.5 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-500 text-rose-600 dark:text-rose-400 hover:text-white border border-rose-200 dark:border-rose-800 hover:border-rose-500 rounded-xl text-xs font-bold shadow-xs flex items-center justify-center gap-2 transition-all active:scale-95"
        >
          <LogOut className="w-4 h-4" />
          <span>{isBn ? 'লগআউট করুন' : 'Sign Out'}</span>
        </button>
      </div>

      {/* Danger Zone: Reset Data */}
      <div className="p-6 bg-rose-500/5 border border-rose-500/20 rounded-3xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-rose-600 dark:text-rose-400 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            <span>{t.resetData}</span>
          </h3>

          <button
            onClick={() => {
              if (window.confirm(t.resetConfirm)) {
                resetData();
              }
            }}
            className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs font-bold shadow-md shadow-rose-500/20 flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>{t.resetData}</span>
          </button>
        </div>
        <p className="text-xs text-rose-500/80 leading-relaxed">{t.resetConfirm}</p>
      </div>

      {/* App Lock PIN Setup/Change Modal */}
      <SetPinModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        mode={pinModalMode}
      />
    </div>
  );
};
